import React, { useState } from "react";
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { getTranslations } from "../../../../translations/Translations";
import {
  EventRouterResponse,
  LocationRecord,
  LocationsRouterResponse,
} from "../../../../interfaces/Interfaces";
import { useRevalidator, useRouteLoaderData } from "react-router-dom";
import { Droppable, Draggable, DragDropContext } from "react-beautiful-dnd";
import LocationForm from "./LocationForm";
import { DirtyFormDialog } from "../../../../components/DirtyFormDialog";

function LocationTab() {
  const translations = getTranslations();
  const { event } = useRouteLoaderData("event") as EventRouterResponse;
  const { locations } = useRouteLoaderData(
    "locations"
  ) as LocationsRouterResponse;
  const [location, setLocation] = useState<LocationRecord | null>(locations[0]);
  const [nextLocation, setNextLocation] = useState<LocationRecord | null>(null);
  const [dirty, setDirty] = useState<Boolean>(false);
  const revalidator = useRevalidator();

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    let data = locations;
    let promises: Promise<Response>[] = [];

    // reorder
    let [draggedObj] = data.splice(result.source.index, 1);
    data.splice(result.destination.index, 0, draggedObj);
    data.forEach((item, index) => {
      item.sortorder = index + 1;
      // save each item
      promises.push(
        fetch("/api/locations/" + item.id, {
          method: "put",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + sessionStorage.getItem("jwt"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(item),
        })
      );
    });

    Promise.all(promises).then(() => {
      revalidator.revalidate();
    });
  };

  const newpartner = () => {
    fetch("/api/locations", {
      method: "post",
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + sessionStorage.getItem("jwt"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        defaultLanguage: event.defaultLanguage,
        language: event.defaultLanguage,
        title: "Neues Veranstaltungsort",
        eventId: event.id,
        sortorder: locations.length,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        let p: LocationRecord = result.data;
        setLocation(p);
        revalidator.revalidate();
      });
  };

  const getButtonClass = (id: number): string => {
    if (location?.id === id) {
      return "cliclalistitembutton active";
    }
    return "cliclalistitembutton";
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      className="collectionedit"
      flex="1"
      overflow="hidden"
    >
      <Box width={"260px"} className="list">
        <Box p={1}>
          <Button variant="contained" onClick={newpartner}>
            {translations["locationtab.add"]}
          </Button>
        </Box>
        <DirtyFormDialog
          open={nextLocation !== null}
          continue={() => {
            setLocation(nextLocation);
            setNextLocation(null);
          }}
          cancel={() => {
            setNextLocation(null);
          }}
        />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="locations" type="location">
            {(provided) => (
              <List
                className="cliclalist"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {locations.map((p: LocationRecord, index: number) => {
                  return (
                    <Draggable
                      key={p.id}
                      draggableId={"location-" + p.id}
                      index={index}
                    >
                      {(provided) => (
                        <ListItemButton
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={getButtonClass(p.id)}
                          onClick={() => {
                            if (dirty) {
                              setNextLocation(p);
                            } else {
                              setLocation(p);
                            }
                          }}
                        >
                          <ListItemText
                            primary={p.translations[p.defaultLanguage].title}
                          />
                        </ListItemButton>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      {!location ? (
        <Box flex="1" p={2}>
          <Typography>{translations["locationtab.empty"]}</Typography>
        </Box>
      ) : null}
      {!!location ? (
        <LocationForm
          key={location.id}
          setDirty={setDirty}
          location={location}
          onDelete={() => {
            fetch("/api/locations/" + location.id, {
              method: "delete",
              headers: {
                Accept: "application/json",
                Authorization: "Bearer " + sessionStorage.getItem("jwt"),
                "Content-Type": "application/json",
              },
            }).then(() => {
              if (location.id === locations[0].id) {
                setLocation(locations[1]);
              } else {
                setLocation(locations[0]);
              }
              revalidator.revalidate();
            });
          }}
        />
      ) : null}
    </Box>
  );
}

export default LocationTab;
